import { useForm } from "@tanstack/react-form";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";
import { Skeleton } from "~/components/ui/skeleton";
import { SuspenseFallback } from "~/components/ui/suspense-fallback";
import { useParameters, useUpdateParameters } from "~/lib/client/parameters";
import {
	DEFAULT_PARAMETERS,
	parametersSchema,
} from "~/lib/server/functions/parameters";
import { m } from "~/i18n/messages";

export const Route = createFileRoute("/app/settings/parameters")({
	component: ParametersPage,
});

function SettingsParametersSkeleton() {
	return (
		<div className="w-full h-full flex flex-col gap-4">
			{/* Parameter fields */}
			{[1, 2, 3].map((i) => (
				<div key={i}>
					<Skeleton className="h-5 w-40 mb-2" />
					<Skeleton className="h-10 w-full mb-1" />
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-3/4" />
				</div>
			))}

			{/* Toggle section */}
			<div className="flex gap-4">
				{[1, 2].map((i) => (
					<div key={i} className="flex-1">
						<Skeleton className="h-5 w-32 mb-2" />
						<Skeleton className="h-6 w-10 mb-1 rounded-full" />
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-5/6" />
					</div>
				))}
			</div>

			{/* Number inputs section */}
			<div className="flex gap-4">
				{[1, 2].map((i) => (
					<div key={i} className="flex-1">
						<Skeleton className="h-5 w-28 mb-2" />
						<Skeleton className="h-10 w-full mb-1" />
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-5/6" />
					</div>
				))}
			</div>

			{/* Buttons */}
			<div className="flex justify-end items-center gap-2 mt-4">
				<Skeleton className="h-10 w-24" />
				<Skeleton className="h-10 w-40" />
			</div>
		</div>
	);
}

function ParametersPage() {
	return (
		<SuspenseFallback
			loading={<SettingsParametersSkeleton />}
			error={
				<div className="p-8 text-red-500">
					{m["settings.srs_parameters.error_loading"]({
						error: "Failed to load parameters",
					})}
				</div>
			}
		>
			<ParametersForm />
		</SuspenseFallback>
	);
}

function ParametersForm() {
	const { data: parameters } = useParameters();
	const updateParametersMutation = useUpdateParameters();

	const form = useForm({
		validators: {
			onChange: parametersSchema,
		},
		defaultValues: parameters ?? DEFAULT_PARAMETERS,
		onSubmit: ({ value }) =>
			toast.promise(
				updateParametersMutation.mutateAsync({
					request_retention: value.request_retention,
					maximum_interval: value.maximum_interval,
					w: value.w,
					enable_fuzz: value.enable_fuzz,
					enable_short_term: value.enable_short_term,
					card_limit: value.card_limit,
					lapses: value.lapses,
				}),
				{
					loading: m["settings.srs_parameters.toast.updating"](),
					success: m["settings.srs_parameters.toast.updated_success"](),
					error: m["settings.srs_parameters.toast.update_failed"](),
				},
			),
	});

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
			className="w-full h-full flex flex-col gap-4"
		>
			<form.Field
				name="request_retention"
				children={(field) => (
					<div>
						<Label className="flex-col items-start">
							{m["settings.srs_parameters.request_retention.label"]()}
							<Input
								type="number"
								value={field.state.value}
								onBlur={field.handleBlur}
								onChange={(e) => field.handleChange(e.target.valueAsNumber)}
							/>
						</Label>
						<small className="text-sm text-muted-foreground">
							{m["settings.srs_parameters.request_retention.description"]()}
						</small>
					</div>
				)}
			/>
			<form.Field
				name="maximum_interval"
				children={(field) => (
					<div>
						<Label className="flex-col items-start">
							{m["settings.srs_parameters.maximum_interval.label"]()}
							<Input
								type="number"
								value={field.state.value}
								onBlur={field.handleBlur}
								onChange={(e) => field.handleChange(e.target.valueAsNumber)}
							/>
						</Label>
						<small className="text-sm text-muted-foreground">
							{m["settings.srs_parameters.maximum_interval.description"]()}
						</small>
					</div>
				)}
			/>
			<form.Field
				name="w"
				children={(field) => (
					<div>
						<Label className="flex-col items-start">
							{m["settings.srs_parameters.weights.label"]()}
							<Input
								value={field.state.value.join(", ")}
								onBlur={field.handleBlur}
								onChange={(e) =>
									field.handleChange(
										e.target.value.split(",").map(Number.parseFloat),
									)
								}
								readOnly
							/>
						</Label>
						<small className="text-sm text-muted-foreground">
							{m["settings.srs_parameters.weights.description"]()}
						</small>
					</div>
				)}
			/>
			<div className="flex gap-4">
				<form.Field
					name="enable_fuzz"
					children={(field) => (
						<div className="flex-1">
							<Label className="flex-col items-start">
								{m["settings.srs_parameters.enable_fuzz.label"]()}
								<Switch
									checked={field.state.value}
									onChange={() => field.handleChange(!field.state.value)}
								/>
							</Label>
							<small className="text-sm text-muted-foreground">
								{m["settings.srs_parameters.enable_fuzz.description"]()}
							</small>
						</div>
					)}
				/>
				<form.Field
					name="enable_short_term"
					children={(field) => (
						<div className="flex-1">
							<Label className="flex-col items-start">
								{m["settings.srs_parameters.enable_short_term.label"]()}
								<Switch
									checked={field.state.value}
									onChange={() => field.handleChange(!field.state.value)}
								/>
							</Label>
							<small className="text-sm text-muted-foreground">
								{m["settings.srs_parameters.enable_short_term.description"]()}
							</small>
						</div>
					)}
				/>
			</div>
			<div className="flex gap-4">
				<form.Field
					name="card_limit"
					children={(field) => (
						<div className="flex-1">
							<Label className="flex-col items-start">
								{m["settings.srs_parameters.card_limit.label"]()}
								<Input
									type="number"
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.valueAsNumber)}
								/>
							</Label>
							<small className="text-sm text-muted-foreground">
								{m["settings.srs_parameters.card_limit.description"]()}
							</small>
						</div>
					)}
				/>
				<form.Field
					name="lapses"
					children={(field) => (
						<div className="flex-1">
							<Label className="flex-col items-start">
								{m["settings.srs_parameters.lapses.label"]()}
								<Input
									type="number"
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.valueAsNumber)}
								/>
							</Label>
							<small className="text-sm text-muted-foreground">
								{m["settings.srs_parameters.lapses.description"]()}
							</small>
						</div>
					)}
				/>
			</div>
			<div className="flex justify-end items-center gap-2 mt-4">
				<form.Subscribe
					selector={(state) => [state.isDirty]}
					children={([isDirty]) => (
						<Button
							type="button"
							variant="secondary"
							onClick={() => form.reset()}
							disabled={!isDirty}
						>
							{m["settings.srs_parameters.reset_button"]()}
						</Button>
					)}
				/>
				<form.Subscribe
					selector={(state) => [state.canSubmit, state.isSubmitting]}
					children={([canSubmit, isSubmitting]) => (
						<Button type="submit" disabled={!canSubmit || isSubmitting}>
							{isSubmitting
								? m["settings.srs_parameters.save_button_saving"]()
								: m["settings.srs_parameters.save_button"]()}
						</Button>
					)}
				/>
			</div>
		</form>
	);
}
